import {
  DeviationSubjectType,
  OrderDeviationType,
  ProjectDeviationType,
  ReceiptDeviationType
} from '../../../shared/deviations/types';
import { DashboardDate } from '../period-pickers/dashboard-day-filter/dashboard-day-filter.component';
import {
  OrderDeliveries,
  OrderStatus,
  Plant,
  PlantCapacity,
  PlantData,
  ProjectDeviations,
  ProjectsOrdersDashboardData
} from './types';

const MOCK_PLANTS: Plant[] = [
  { id: 1, code: 'ZWO', name: 'Zwolle' },
  { id: 2, code: 'OLS', name: 'Olst' },
  { id: 3, code: 'DEV', name: 'Deventer' },
  { id: 4, code: 'APE', name: 'Apeldoorn' },
  { id: 5, code: 'ALM', name: 'Almelo' },
  { id: 6, code: 'ENS', name: 'Enschede' },
  { id: 7, code: 'HAR', name: 'Harderwijk' }
];

const PROJECT_DEVIATION_TYPES: Array<Omit<ProjectDeviations, 'count'>> = [
  { subjectType: DeviationSubjectType.project, type: ProjectDeviationType.missing_location },
  { subjectType: DeviationSubjectType.project, type: ProjectDeviationType.missing_travel_info },
  { subjectType: DeviationSubjectType.order, type: OrderDeviationType.missing_recipe },
  { subjectType: DeviationSubjectType.order, type: OrderDeviationType.not_planned },
  { subjectType: DeviationSubjectType.order, type: OrderDeviationType.multi_day_price_changed },
  { subjectType: DeviationSubjectType.order, type: OrderDeviationType.not_produced },
  { subjectType: DeviationSubjectType.order, type: OrderDeviationType.partially_produced },
  { subjectType: DeviationSubjectType.order, type: OrderDeviationType.overproduced },
  { subjectType: DeviationSubjectType.order, type: OrderDeviationType.other_producing_plant },
  { subjectType: DeviationSubjectType.order, type: OrderDeviationType.overdue },
  { subjectType: DeviationSubjectType.order, type: OrderDeviationType.lagging_behind },
  { subjectType: DeviationSubjectType.order, type: OrderDeviationType.missing_price_usage },
  { subjectType: DeviationSubjectType.receipt, type: ReceiptDeviationType.cancelled },
  { subjectType: DeviationSubjectType.receipt, type: ReceiptDeviationType.dropped },
  { subjectType: DeviationSubjectType.receipt, type: ReceiptDeviationType.missing_amount }
];

export function createProjectsOrdersMockData(
  date: DashboardDate,
  selectedPlantId: number = -1
): ProjectsOrdersDashboardData {
  const random = createRandom(hashString(date.dateYMD));
  const deviations: ProjectDeviations[] = [];
  const plantsData: PlantData[] = [];
  const ordersStatusByPlant: ProjectsOrdersDashboardData['ordersStatusByPlant'] = {};
  const orderDeliveries: OrderDeliveries = {
    summary: { running: 0, upcoming: 0, onCall: 0, overdue: 0 },
    items: []
  };
  const capacities: PlantCapacity[] = [];
  let onCallOrderCount = 0;
  let onCallAmount = 0;

  for(const plant of MOCK_PLANTS) {
    const projectCount = randomInt(random, 3, 7);
    const orderCount = randomInt(random, projectCount + 2, projectCount * 4);
    const projectDeviations = createPlantDeviations(projectCount, random);
    if(selectedPlantId <= 0 || selectedPlantId === plant.id)
      mergeDeviationCounts(deviations, projectDeviations);

    const plannedAmount = randomFloat(random, 100, 280, 1);
    const deliveredAmount = randomFloat(random, 0, plannedAmount, 1);
    const remainingAmount = round(plannedAmount - deliveredAmount, 1);
    const progressPercentage = percentage(deliveredAmount, plannedAmount);
    const deviationCount = projectDeviations.reduce(
      (total, deviation) => total + deviation.count,
      0
    );

    plantsData.push({
      ...plant,
      summary: {
        plannedAmount,
        deliveredAmount,
        remainingAmount,
        progressPercentage,
        projectCount,
        orderCount,
        deviationCount
      }
    });

    const completed = randomInt(random, 0, Math.floor(orderCount * .55));
    const partlyDelivered = randomInt(random, 0, orderCount - completed);
    const notStarted = orderCount - completed - partlyDelivered;
    const onCall = randomInt(random, 0, Math.min(4, orderCount));
    const receiptsCount = randomInt(random, deliveredAmount > 0 ? 1 : 0, Math.max(1, orderCount * 2));
    ordersStatusByPlant[plant.id] = {
      orderCount,
      completed,
      completedPercentage: percentage(completed, orderCount),
      partlyDelivered,
      partlyDeliveredPercentage: percentage(partlyDelivered, orderCount),
      notStarted,
      notStartedPercentage: percentage(notStarted, orderCount),
      onCall,
      receiptsCount
    };

    const plantDeliveries = randomInt(random, 2, 5);
    for(let index = 0; index < plantDeliveries; index++) {
      const status = randomItem(random, [
        OrderStatus.running,
        OrderStatus.upcoming,
        OrderStatus.onCall,
        OrderStatus.laggingBehind
      ]);
      const itemPlannedAmount = randomFloat(random, 8, 40, 1);
      const itemDeliveredAmount = status === OrderStatus.upcoming || status === OrderStatus.onCall
        ? 0
        : randomFloat(random, 0, itemPlannedAmount, 1);
      const projectId = plant.id * 100 + index + 1;
      const orderId = plant.id * 10000 + index + 1;

      orderDeliveries.items.push({
        order: { id: orderId, description: `Order ${orderId}` },
        orderId,
        time: `${String(7 + index).padStart(2, '0')}:${randomItem(random, ['00', '15', '30', '45'])}`,
        plant,
        project: { id: projectId, name: `Project ${plant.code}-${index + 1}` },
        plannedAmount: itemPlannedAmount,
        deliveredAmount: itemDeliveredAmount,
        remainingAmount: round(itemPlannedAmount - itemDeliveredAmount, 1),
        status
      });

      if(status === OrderStatus.running)
        orderDeliveries.summary.running++;
      else if(status === OrderStatus.upcoming)
        orderDeliveries.summary.upcoming++;
      else if(status === OrderStatus.onCall)
        orderDeliveries.summary.onCall++;
      else
        orderDeliveries.summary.overdue++;
    }

    const productionCapacity = randomInt(random, 55, 95);
    capacities.push({
      plant,
      productionCapacity,
      periods: ['09:00', '10:00', '11:00', '12:00', '13:00', '14:00', '15:00'].map((timeFrom, index) => {
        const planned = randomFloat(random, productionCapacity * .35, productionCapacity * 1.15, 1);
        return {
          timeFrom,
          timeTo: `${String(10 + index).padStart(2, '0')}:00`,
          plannedAmount: planned,
          loadPercentage: percentage(planned, productionCapacity)
        };
      })
    });

    onCallOrderCount += onCall;
    onCallAmount += randomFloat(random, onCall * 4, onCall * 12, 1);
  }

  return {
    plants: MOCK_PLANTS.map(plant => ({ ...plant })),
    plantsData,
    deviations,
    ordersStatusByPlant,
    orderDeliveries,
    plantCapacities: {
      onCall: {
        orderCount: onCallOrderCount,
        amount: round(onCallAmount, 1)
      },
      plants: capacities
    }
  };
}

function createPlantDeviations(
  projectCount: number,
  random: () => number
): ProjectDeviations[] {
  return PROJECT_DEVIATION_TYPES.map(deviation => ({
    ...deviation,
    count: randomInt(random, 1, Math.max(1, projectCount))
  }));
}

function mergeDeviationCounts(
  target: ProjectDeviations[],
  source: ProjectDeviations[]
): void {
  for(const deviation of source) {
    const current = target.find(item =>
      item.subjectType === deviation.subjectType && item.type === deviation.type
    );
    if(current)
      current.count += deviation.count;
    else
      target.push({ ...deviation });
  }
}


function createRandom(seed: number): () => number {
  let value = seed >>> 0;
  return () => {
    value += 0x6D2B79F5;
    let result = value;
    result = Math.imul(result ^ result >>> 15, result | 1);
    result ^= result + Math.imul(result ^ result >>> 7, result | 61);
    return ((result ^ result >>> 14) >>> 0) / 4294967296;
  };
}

function hashString(value: string): number {
  let hash = 2166136261;
  for(let index = 0; index < value.length; index++) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return hash >>> 0;
}

function randomInt(random: () => number, min: number, max: number): number {
  return Math.floor(random() * (max - min + 1)) + min;
}

function randomFloat(random: () => number, min: number, max: number, decimals: number): number {
  return round(min + random() * (max - min), decimals);
}

function randomItem<T>(random: () => number, items: T[]): T {
  return items[randomInt(random, 0, items.length - 1)];
}

function percentage(value: number, total: number): number {
  return total > 0 ? Math.round(value / total * 100) : 0;
}

function round(value: number, decimals: number): number {
  const multiplier = Math.pow(10, decimals);
  return Math.round(value * multiplier) / multiplier;
}
